import { createNoise2D } from "simplex-noise"
import { createEffect, createMemo, DEV } from "solid-js"
import {
  BufferAttribute,
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshLambertMaterial
} from "three"
import { useNodeNeighborBaseFloorElevationReactiveMap } from "../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import type { Engine } from "../../../core/engine/Engine.type"
import getEightTilePositionNeighborsList from "../../../core/engine/TileGraphNode/getEightTilePositionNeighborsList"
import type { TileGraphNode } from "../../../core/engine/TileGraphNode/TileGraphNode.type"
import isEntityLayerIdTerrain1 from "../../../core/entity/EntityLayerId/isEntityLayerIdTerrain1"
import { isEntityDesert, isEntityShoal } from "../../../core/entity/entityTypeMetaList.generated"
import type { Entity } from "../../../core/entity/index"
import { createNewRNGConfig, next } from "../../../core/rng/index"
import { abs, max } from "../../../core/util/math/Math"
import { freeze } from "../../../core/util/Object"
import noop from "../../../ldsh/noop"
import { immutableEmptyArray } from "../../../lib"
import { VERTICES_PER_TILE } from "../../consts"
import runWithObject3DOwner from "../../fn/runWithObject3DOwner"
import InwardPointedPlaneGeometry from "../mesh/InwardPointedPlaneGeometry.class"
import calculateNodeVertexOutputColor from "./calculateNodeVertexOutputColor"
import calculateShoalNodeVertexOutputColor from "./calculateShoalNodeVertexOutputColor"
import createNodeNeighborAvgColorCollection from "./createNodeNeighborAvgColorCollection"
import createNodeNeighborBaseColorCollection from "./createNodeNeighborBaseColorCollection"
import createTerrainMesh1DetailPropsEffect from "./createTerrainMesh1DetailPropsEffect"
import getDesertSandColor2 from "./getDesertSandColor2"
import getNeighborWeightsV1Cached from "./getNeighborWeightsV1Cached"
import getShoalSandColor2 from "./getShoalSandColor2"
import getTerrainMesh1Z from "./getTerrainMesh1Z"

const segments = 64 * 0.5
const size = (VERTICES_PER_TILE * 1)
const halfSize = size / 2

const outColor = new Color()

const groupRng = createNewRNGConfig()
const groupNoise2D = createNoise2D(() => next(groupRng))

const wzRatio = 1 / 16

const sandDuneTop = new Color(0xF7DC8A)
if (DEV) {
  freeze(sandDuneTop)
}
const wetSand = new Color(0xD4AC0D)
if (DEV) {
  freeze(wetSand)
}

export default function createTerrainMesh1V2Group(
  engine: Engine,
  tpNodeList: Array<TileGraphNode>,
): Group {
  // console.log("createTerrainMesh1V2Group")
  // console.time("createTerrainMesh1V2Group")
  const group = new Group()
  group.name = "TerrainMesh1"
  const neighborBaseElevationReactiveMap = useNodeNeighborBaseFloorElevationReactiveMap(engine)

  tpNodeList.forEach((node) => {
    const nodeIndex = node.index
    const material = new MeshLambertMaterial({
      vertexColors: true,
      
    })

    const getEnt = createMemo(() => node.ents().find(isEntityLayerIdTerrain1))

    // const getNoise2D = createMemo(() => createEntityNoise2D(getEnt()))
    // const getColor = createMemo(() => getTerrainMesh1TileColor(getEnt()))
    // console.log(entityDisplayName(getEnt()))

    // const geometry = new PlaneGeometry(size, size, segments, segments)
    // console.log('size', size, 'segments', segments)
    const geometry = new InwardPointedPlaneGeometry(size, size, segments, segments)
    // geometry.rotateZ(HALF_PI)

    const positionAttr = geometry.attributes.position
    const colorAttr = new Float32Array(positionAttr.count * 3)

    geometry.setAttribute("color", new BufferAttribute(colorAttr, 3))

    const mesh = new Mesh(geometry, material)
    mesh.userData.node = node
    mesh.name = `X${node.x}Y${node.y}L1`
    mesh.receiveShadow = true
    mesh.castShadow = false

    const rh = 0 * (VERTICES_PER_TILE / 32)
    mesh.position.set(node.x * VERTICES_PER_TILE, node.y * -VERTICES_PER_TILE, rh)
    group.add(mesh)

    // const noise2D = getNoise2D()
    // const baseColor = getColor()

    // Reactive effect for color change
    runWithObject3DOwner(mesh, () => {
      createEffect(() => {
        const ent = getEnt()
        // console.time(`Node${node.index}`)
        // const isShoalEvent = ent && isEntityShoal(ent)
        // const isDesertEvent = ent && isEntityDesert(ent)
        // const noise2D = getNoise2D()
        // const baseColor = getColor()

        const eightNeighbors = getEightTilePositionNeighborsList(node)
        const baseElevationsCollection = neighborBaseElevationReactiveMap.get(nodeIndex)
        if (!baseElevationsCollection) {
          // console.timeEnd(`Node${node.index}`)
          return
        }
        const eightNeighborColorCollection = createNodeNeighborBaseColorCollection(node, eightNeighbors)
        const eightNeighborAvgColorCollection = createNodeNeighborAvgColorCollection(eightNeighborColorCollection)
        const eightNeighborSameTypeList = ent ? eightNeighbors.map((neighbor) => {
          return neighbor?.ents().find(ent2 => ent2.etype_id === ent.etype_id)
        }) : (immutableEmptyArray as unknown as Array<Entity>)

        // const baseElevation = getTerrainMesh1NodeElevation(node)
        // const nElevation = getTerrainMesh1NodeElevation(node.n)
        // const sElevation = getTerrainMesh1NodeElevation(node.s)
        // const eElevation = getTerrainMesh1NodeElevation(node.e)
        // const wElevation = getTerrainMesh1NodeElevation(node.w)

        const positionAttrLen = positionAttr.count

        // const minX = positionAttr.getX(0)
        // const maxX = positionAttr.getX(positionAttrLen - 1)
        // const minY = positionAttr.getY(0)
        // const maxY = positionAttr.getY(positionAttrLen - 1)

        // Modify vertices with noise and assign vertex colors
        for (let i = 0; i < positionAttrLen; i++) {
          const x = positionAttr.getX(i)
          const y = positionAttr.getY(i)

          const nx = x / halfSize
          const ny = y / halfSize
          const vertexWeights = getNeighborWeightsV1Cached(nx, ny)
          // const vertexElevation = calculateNodeVertexElevation(vertexWeights, baseElevationsCollection)

          // const isEdge = (x === minX || x === maxX || y === minY || y === maxY)
          // const zMult = (!isEdge && (isShoalEvent || isDesertEvent)) ? .1 : .05
          // const zMult = .05

          // world x and y
          const wx = (node.x * VERTICES_PER_TILE) + x
          const wy = (node.y * -VERTICES_PER_TILE) + y

          const noise1 = groupNoise2D(wx * wzRatio, wy * wzRatio)
          // const noise2 = ((noise1 + 1) / 2) * 1
          // const z0 = weightedElevation(x, y, noise1, noise2) + (noise1 * zMult)
          // if (z0 !== (vertexElevation + (noise1 * zMult))) {
          //   console.log({ x, y }, { nx, ny })
          //   throw new Unexpected(`z0 !== vertexElevation: ${z0} !== ${vertexElevation}`)
          // }
          // const z = weightedElevation(x, y, noise1, noise2) + (noise1 * zMult)
          // const z0 = vertexElevation + (noise1 * zMult)
          const z = getTerrainMesh1Z(baseElevationsCollection, x, y)
          positionAttr.setZ(i, z)

          if (ent && (isEntityShoal(ent) || isEntityDesert(ent))) {
            writeNoiseTintColor(ent, noise1, outColor)
            calculateShoalNodeVertexOutputColor(vertexWeights, eightNeighborAvgColorCollection, eightNeighborSameTypeList, outColor)
          } else {
            calculateNodeVertexOutputColor(vertexWeights, eightNeighborAvgColorCollection, outColor)
          }

          // outColor.setHex(0xffffff).lerp(new Color(0x000000), ((z + 0.5) / 1.125))

          // TODO use outColor

          // tmpColor.copy(baseColor)
          // tmpColor.multiplyScalar(noise2)
          // tmpColor.lerp(baseColor, 0.8)

          // tmpColor.copy(outColor)
          // tmpColor.multiplyScalar(noise2)
          // outColor.lerp(tmpColor, 0.3)

          // noiseTintColor(1 + z)
          // noiseTintColor(1.5 * normalizeToMinusOneToOne(z))
          // console.log(outColor.toArray())

          colorAttr[i * 3] = outColor.r
          colorAttr[i * 3 + 1] = outColor.g
          colorAttr[i * 3 + 2] = outColor.b
        }
        geometry.computeVertexNormals()
        geometry.attributes.position.needsUpdate = true
        geometry.attributes.color.needsUpdate = true

        if (DEV) {
          material.side = DoubleSide
        }

        material.needsUpdate = true
        // material.wireframe = true
        // console.timeEnd(`Node${node.index}`)
        // console.log('here'); return
      })
    })

    // eslint-disable-next-line solid/reactivity
    createTerrainMesh1DetailPropsEffect(neighborBaseElevationReactiveMap, nodeIndex, mesh, getEnt)
  })

  // setTimeout(() => {
  //   // min: -1.0498933257291931, max: 0.5304222469368381
  //   const { min, max } = getNumberArrayMinMax(zValues)

  //   console.log({ min, max })
  //   console.log(normalizeToMinusOneToOne(min!), normalizeToMinusOneToOne(max!))
  //   zValues.length = 0
  // }, 100)
  // console.timeEnd("createTerrainMesh1V2Group")

  return group
}

function writeNoiseTintColor (ent : Entity, nz: number, outColor: Color) {
  if (isEntityShoal(ent)) {
    if (nz > 0) {
      nz *= nz
    } else {
      nz *= abs(nz)
      nz = max(nz, -0.9)
    }
    getShoalSandColor2(nz, outColor)
  }
  if (isEntityDesert(ent)) {
    nz *= 0.5
    nz += 0.2
    // if (nz > 0) {
    //   // nz *= (nz * 2)
    // } else {
    //   // nz = max(nz, -0.9)
    //   // nz *= abs(nz)
    // }
    getDesertSandColor2(nz, outColor)
  }
  return noop
}